// src/services/api.service.js
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

// ============================================================================
// CONFIGURATION
// ============================================================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:60239';
const APP_ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

// Log de démarrage avec validation
console.log('🔧 API Service initialized:', {
  baseURL: API_BASE_URL,
  env: APP_ENV,
  nodeEnv: process.env.NODE_ENV,
  timestamp: new Date().toISOString()
});

// Validation des variables d'environnement critiques
if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn('⚠️ REACT_APP_API_BASE_URL non définie, utilisation du fallback:', API_BASE_URL);
}

// ============================================================================
// INSTANCE AXIOS
// ============================================================================

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 15000
});

// ============================================================================
// GESTION DES TOKENS
// ============================================================================

/**
 * Récupère le token d'accès depuis localStorage
 */
const getToken = () => {
  try {
    return localStorage.getItem('access_token');
  } catch (error) {
    console.error('❌ Erreur lecture token:', error);
    return null;
  }
};

/**
 * Récupère le refresh token depuis localStorage
 */
const getRefreshToken = () => {
  try {
    return localStorage.getItem('refresh_token');
  } catch (error) {
    console.error('❌ Erreur lecture refresh token:', error);
    return null;
  }
};

/**
 * Sauvegarde les tokens dans localStorage
 */
const saveTokens = (accessToken, refreshToken) => {
  try {
    if (accessToken) {
      localStorage.setItem('access_token', accessToken);
      console.log('✅ Access token sauvegardé');
    }
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
      console.log('✅ Refresh token sauvegardé');
    }
  } catch (error) {
    console.error('❌ Erreur sauvegarde tokens:', error);
  }
};

/**
 * Supprime tous les tokens et données utilisateur
 */
const clearTokens = () => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('csrfToken');
    console.log('🗑️ Tokens supprimés');
  } catch (error) {
    console.error('❌ Erreur suppression tokens:', error);
  }
};

/**
 * Vérifie si le token est valide et non expiré
 */
const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    
    // Vérifier l'expiration avec une marge de 30 secondes
    return decoded.exp > (currentTime + 30);
  } catch (error) {
    console.error('❌ Token invalide:', error);
    return false;
  }
};

/**
 * Vérifie si le token expire bientôt (dans les 5 minutes)
 */
const isTokenExpiringSoon = (token) => {
  if (!token) return false;
  
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const fiveMinutes = 5 * 60;
    
    return decoded.exp < (currentTime + fiveMinutes);
  } catch (error) {
    return false;
  }
};

// ============================================================================
// INTERCEPTEUR DE REQUÊTES
// ============================================================================

apiClient.interceptors.request.use(
  async (config) => {
    // Génération d'un ID unique pour la requête (pour le debug)
    const requestId = Date.now().toString(36) + Math.random().toString(36).substring(2);
    config.headers['X-Request-ID'] = requestId;

    // Ne pas ajouter de token pour les routes publiques
    const publicRoutes = ['/auth/login', '/auth/register', '/auth/refresh-token', '/auth/confirm-email'];
    const isPublicRoute = publicRoutes.some(route => config.url?.includes(route));

    if (!isPublicRoute) {
      const token = getToken();
      
      if (token) {
        // Vérifier la validité du token
        if (isTokenValid(token)) {
          config.headers.Authorization = `Bearer ${token}`;
          
          // Log si le token expire bientôt
          if (isTokenExpiringSoon(token)) {
            console.warn('⚠️ Token expire bientôt, considérez un rafraîchissement');
          }
        } else {
          console.warn('⚠️ Token expiré détecté dans la requête');
          // Ne pas ajouter le token expiré, laisser le serveur renvoyer 401
        }
      } else if (config.url && !config.url.includes('/auth/')) {
        console.warn('⚠️ Pas de token disponible pour une route protégée');
      }
    }

    // Ajouter CSRF token pour les méthodes de modification
    const modifyingMethods = ['post', 'put', 'delete', 'patch'];
    if (modifyingMethods.includes(config.method?.toLowerCase())) {
      const csrfToken = localStorage.getItem('csrfToken');
      if (csrfToken && csrfToken !== 'disabled') {
        config.headers['x-csrf-token'] = csrfToken;
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    // Log de débogage en développement
    if (APP_ENV === 'development') {
      console.log(`🔄 [${requestId}] ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data
      });
    }

    return config;
  },
  (error) => {
    console.error('❌ Erreur intercepteur requête:', error);
    return Promise.reject(error);
  }
);

// ============================================================================
// INTERCEPTEUR DE RÉPONSES
// ============================================================================

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    const requestId = response.config?.headers?.['X-Request-ID'] || 'unknown';
    
    // Log de débogage en développement
    if (APP_ENV === 'development') {
      console.log(`✅ [${requestId}] Réponse ${response.status}`, response.data);
    }

    // Vérifier si un nouveau token a été fourni par le serveur
    const newToken = response.headers['x-new-token'] || response.headers['X-New-Token'];
    if (newToken) {
      saveTokens(newToken);
      console.log('🔄 Token JWT rafraîchi automatiquement via header');
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const requestId = originalRequest?.headers?.['X-Request-ID'] || 'unknown';
    
    // ========================================================================
    // GESTION ERREUR RÉSEAU
    // ========================================================================
    if (!error.response) {
      console.error(`❌ [${requestId}] Erreur réseau:`, error.message);
      
      const networkError = {
        message: 'Impossible de contacter le serveur. Vérifiez votre connexion internet.',
        code: 'NETWORK_ERROR',
        originalError: error
      };
      
      return Promise.reject(networkError);
    }
    
    const status = error.response.status;
    const errorData = error.response.data;
    
    console.error(`❌ [${requestId}] Erreur ${status}:`, errorData);
    
    // ========================================================================
    // GESTION ERREUR 401 - NON AUTORISÉ
    // ========================================================================
    if (status === 401 && !originalRequest._retry) {
      // Si déjà en train de rafraîchir, mettre en queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      
      if (!refreshToken) {
        console.error('❌ Pas de refresh token disponible');
        isRefreshing = false;
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        console.log('🔄 Tentative de rafraîchissement du token...');
        
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/refresh-token`,
          { refresh_token: refreshToken },
          { 
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        const { access_token, refresh_token: newRefreshToken } = response.data.data || response.data;
        
        if (!access_token) {
          throw new Error('Pas de token dans la réponse');
        }
        
        saveTokens(access_token, newRefreshToken);
        
        console.log('✅ Token rafraîchi avec succès');
        
        // Mettre à jour le header de la requête originale
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        
        // Traiter la queue
        processQueue(null, access_token);
        isRefreshing = false;
        
        // Réessayer la requête originale
        return apiClient(originalRequest);
        
      } catch (refreshError) {
        console.error('❌ Échec du rafraîchissement du token:', refreshError);
        
        processQueue(refreshError, null);
        isRefreshing = false;
        
        clearTokens();
        
        // Rediriger vers la page de connexion
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      }
    }
    
    // ========================================================================
    // GESTION ERREUR 403 - INTERDIT
    // ========================================================================
    if (status === 403) {
      const errorMessage = errorData?.message || 'Accès refusé';
      
      console.error('❌ Accès refusé:', errorMessage);
      
      // Si c'est un problème de rôle, déconnecter
      if (
        errorMessage.includes('Rôles autorisés') || 
        errorMessage.includes('Accès refusé') ||
        errorMessage.includes('role') ||
        errorMessage.includes('permission')
      ) {
        console.error('❌ Erreur de rôle/permission - Déconnexion');
        clearTokens();
        window.location.href = '/login';
      }
    }
    
    // ========================================================================
    // GESTION ERREUR 429 - TOO MANY REQUESTS
    // ========================================================================
    if (status === 429) {
      console.warn('⚠️ Trop de requêtes - Rate limit atteint');
    }
    
    // ========================================================================
    // GESTION ERREUR 500+ - ERREUR SERVEUR
    // ========================================================================
    if (status >= 500) {
      console.error('❌ Erreur serveur:', status);
    }
    
    // Retourner l'erreur formatée
    return Promise.reject(errorData || {
      message: error.message || 'Une erreur est survenue',
      status: status,
      code: errorData?.code || 'UNKNOWN_ERROR'
    });
  }
);

// ============================================================================
// FONCTIONS UTILITAIRES EXPORTÉES
// ============================================================================

/**
 * Définit le token d'authentification
 */
export const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('access_token', token);
    console.log('✅ Token d\'authentification défini');
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
    clearTokens();
    console.log('🗑️ Token d\'authentification supprimé');
  }
};

/**
 * Vérifie si le token actuel est valide
 */
export const isAuthenticated = () => {
  const token = getToken();
  return isTokenValid(token);
};

/**
 * Récupère les informations du token décodé
 */
export const getTokenInfo = () => {
  const token = getToken();
  if (!token) return null;
  
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('❌ Erreur décodage token:', error);
    return null;
  }
};

/**
 * Déconnexion manuelle
 */
export const logout = () => {
  clearTokens();
  delete apiClient.defaults.headers.common['Authorization'];
  console.log('👋 Déconnexion effectuée');
  window.location.href = '/login';
};

// ============================================================================
// EXPORTS
// ============================================================================

export { 
  clearTokens, 
  saveTokens, 
  getToken, 
  getRefreshToken,
  isTokenValid,
  isTokenExpiringSoon
};

export default apiClient;